import Link from "next/link";
import type { ExploreStoryCard } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";

const MATERIAL_TYPE_LABELS: Partial<
  Record<ExploreStoryCard["materialType"], string>
> = {
  PROMPT: "Prompt",
  WORKFLOW: "Workflow",
  RELEASE: "Release",
  DEAL: "Deal",
};

interface FreshStorySidebarRowProps {
  story: ExploreStoryCard;
  exploreBase: string;
  showSummary?: boolean;
  stretch?: boolean;
}

/**
 * Compact text-only card for the Fresh Stories sidebar (7+ stories layout).
 * No image — shows badge, category/tag, headline, and time.
 *
 * When `showSummary` is true, a 2-line summary is rendered between headline
 * and meta. When `stretch` is true, the row grows to fill the parent column,
 * matching the height of the adjacent featured card.
 */
export function FreshStorySidebarRow({
  story,
  exploreBase,
  showSummary = false,
  stretch = false,
}: FreshStorySidebarRowProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const materialLabel = MATERIAL_TYPE_LABELS[story.materialType];
  const badgeStyleClass = STORY_BADGE_STYLES[story.storyBadge];

  return (
    <Link
      className={`group hover:-translate-y-1 flex flex-col gap-1.5 rounded-xl border border-[var(--explore-card-border)] bg-[var(--explore-bg-card)] p-3 no-underline shadow-[var(--explore-card-shadow)] transition-[transform,box-shadow] duration-300 ease-out hover:shadow-[var(--explore-card-shadow-hover)] active:scale-[0.98] ${
        stretch ? "flex-1" : ""
      }`}
      href={`${exploreBase}/stories/${story.storyId}`}
      prefetch={false}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Headline */}
      <h4
        className="line-clamp-2 text-pretty font-semibold text-[1rem] text-[var(--explore-text-primary)] leading-[1.35]"
        style={{ fontFamily: "var(--explore-card-title-stack)" }}
      >
        {story.headline}
      </h4>

      {/* Optional summary — shown when sidebar has fewer rows so each row
          can fill the height of the adjacent featured card. */}
      {showSummary && story.summary && (
        <p className="line-clamp-2 text-[0.82rem] text-[var(--explore-text-secondary)] leading-[1.45]">
          {story.summary}
        </p>
      )}

      {/* Meta row: badge + category/tag + time */}
      <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
        {materialLabel && (
          <span
            className={`inline-flex items-center rounded-sm px-2 py-0.5 font-bold text-[0.6rem] uppercase tracking-[0.05em] ${badgeStyleClass}`}
          >
            {materialLabel}
          </span>
        )}
        {story.categoryEmoji && (
          <span className="inline-flex items-center justify-center rounded-sm bg-[var(--explore-tag-bg)] px-1.5 py-0.5 text-[0.75rem]">
            {story.categoryEmoji}
          </span>
        )}
        {primaryTag && (
          <span className="inline-flex items-center rounded-sm bg-[var(--explore-tag-bg)] px-2 py-0.5 font-medium text-[0.72rem] text-[var(--explore-tag-text)]">
            {primaryTag.displayName}
          </span>
        )}
        <span className="text-[0.72rem] text-[var(--explore-text-muted)]">
          {story.timeAgo}
        </span>
      </div>
    </Link>
  );
}
